import { PaletteMode } from '@mui/material';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Filiale } from '../../interface/Filiale';
import LoginData from '../../interface/LoginData';
import { StatoB2b } from '../../interface/StatoB2b';
import { Fornitor_table_interface } from '@bludata/common-interfaces';
import BluBookingLoginInterface from '../../interface/BluBookingLoginInterface';
import { isTablet } from 'react-device-detect';
import { isIpadOs } from '@bludata/utility-functions';

export interface RootSliceState {
  listaFiliali: Filiale[];
  listaFornitori: Fornitor_table_interface[];
  themeMode: PaletteMode;
  loginData: LoginData;
  loading: boolean;
  error: string;
  blubookingLoginData?: BluBookingLoginInterface;
  menuFocusOpened: boolean;
  sessioneScaduta: boolean;
  ramoModifica?: {
    parent: string;
    child: string;
  };
  login: {
    username: string;
    password: string;
    azienda: string;
    accessToken: string;
    accessServer: string;
  };
  globalLoading?: boolean;
  openChatAi: boolean;
  crmApiError: boolean;
  imoApiError: boolean;
  diaryApiError: boolean;
  feApiError: boolean;
  keepSessionAlive: boolean;
}

const initialState: RootSliceState = {
  listaFiliali: [],
  listaFornitori: [],
  themeMode: 'light',
  crmApiError: false,
  imoApiError: false,
  diaryApiError: false,
  feApiError: false,
  loginData: {
    $FocusCloudStatus: '',
    Admin: false,
    AgendaNoDefault: false,
    AppFirma: false,
    Chiave: '',
    CodiceInterno: '',
    CodiceProfilo: '',
    CodiceUtente: '',
    DisabilitaBustaOrdini: false,
    Filiale: '',
    Foto: '',
    IMOAttivo: false,
    IsMagFranchisee: false,
    LastRelease: '',
    Lingua: '',
    Localizzazione: '',
    NomeFiliale: '',
    Operatore: '',
    OpzioniCliente: '',
    Plugin: [],
    Profilo: null,
    Programma: '',
    RagioneSociale: '',
    Scaduta: false,
    Tipo: '',
    Versione: '',
    VersioneDB: '',
    VisionCard: false,
    menuFWeb: [],
    menuConfigurazioneFWeb: [],
    partitaIva: '',
    $FocusCloudRouteName: '',
  },
  loading: false,
  error: '',
  menuFocusOpened: !isTablet && !isIpadOs,
  sessioneScaduta: false,
  login: {
    username: '',
    password: '',
    azienda: '',
    accessToken: '',
    accessServer: '',
  },
  openChatAi: false,
  globalLoading: false,
  keepSessionAlive: false,
};

export const RootSlice = createSlice({
  name: 'root',
  initialState,
  reducers: {
    setListaFiliali(state: RootSliceState, action: PayloadAction<Filiale[]>) {
      state.listaFiliali = action.payload;
    },
    setListaFornitori(
      state: RootSliceState,
      action: PayloadAction<Fornitor_table_interface[]>,
    ) {
      state.listaFornitori = action.payload;
    },
    setThemeMode(state: RootSliceState, action: PayloadAction<PaletteMode>) {
      state.themeMode = action.payload;
    },
    setLogin(
      state: RootSliceState,
      action: PayloadAction<{
        username: string;
        password: string;
        azienda: string;
        accessToken: string;
        accessServer: string;
      }>,
    ) {
      state.login = action.payload;
    },
    setCrmApiError(state: RootSliceState, action: PayloadAction<boolean>) {
      state.crmApiError = action.payload;
    },
    setImoApiError(state: RootSliceState, action: PayloadAction<boolean>) {
      state.imoApiError = action.payload;
    },
    setDiaryApiError(state: RootSliceState, action: PayloadAction<boolean>) {
      state.diaryApiError = action.payload;
    },
    setFeApiError(state: RootSliceState, action: PayloadAction<boolean>) {
      state.feApiError = action.payload;
    },
    setAccessToken(state: RootSliceState, action: PayloadAction<string>) {
      state.login.accessToken = action.payload;
    },
    setUsername(state: RootSliceState, action: PayloadAction<string>) {
      state.login.username = action.payload;
    },
    setLoginData(state: RootSliceState, action: PayloadAction<LoginData>) {
      state.loginData = {
        ...action.payload,
        menuConfigurazioneFWeb: action.payload.menuConfigurazioneFWeb ?? [],
      };
    },
    setLoading(state: RootSliceState, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state: RootSliceState, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    setBluBookingLoginData(
      state: RootSliceState,
      action: PayloadAction<BluBookingLoginInterface>,
    ) {
      state.blubookingLoginData = action.payload;
    },
    setMenuFocusOpened(state: RootSliceState, action: PayloadAction<boolean>) {
      state.menuFocusOpened = action.payload;
    },
    setFotoUtente(state: RootSliceState, action: PayloadAction<string>) {
      state.loginData.Foto = action.payload;
    },
    setSessioneScaduta(state: RootSliceState, action: PayloadAction<boolean>) {
      state.sessioneScaduta = action.payload;
    },
    setRamoFocusModifica(
      state: RootSliceState,
      action: PayloadAction<{ parent: string; child: string }>,
    ) {
      state.ramoModifica = action.payload;
    },
    setOpenChatAi(state: RootSliceState, action: PayloadAction<boolean>) {
      state.openChatAi = action.payload;
    },
    setGlobalLoading(state: RootSliceState, action: PayloadAction<boolean>) {
      state.globalLoading = action.payload;
    },
    setKeepSessionAlive(state: RootSliceState, action: PayloadAction<boolean>) {
      state.keepSessionAlive = action.payload;
    },
    setStatoB2b(state: RootSliceState, action: PayloadAction<StatoB2b>) {
      state.loginData.statoB2b = action.payload;
    },
    setLastRelease(state: RootSliceState, action: PayloadAction<string>) {
      state.loginData.LastRelease = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setKeepSessionAlive,
  setThemeMode,
  setRamoFocusModifica,
  setLoginData,
  setLoading,
  setError,
  setListaFiliali,
  setAccessToken,
  setUsername,
  setListaFornitori,
  setBluBookingLoginData,
  setMenuFocusOpened,
  setFotoUtente,
  setSessioneScaduta,
  setLogin,
  setOpenChatAi,
  setCrmApiError,
  setImoApiError,
  setDiaryApiError,
  setFeApiError,
  setGlobalLoading,
  setStatoB2b,
  setLastRelease,
} = RootSlice.actions;

export default RootSlice.reducer;
